export class YZElement extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.observers = []; this.timers = new Set();
    try { this.init?.(); } catch (error) { this.destroy(); if (window.Shopify?.designMode) console.error(error); }
  }
  listen(target, type, listener, options = {}) { target.addEventListener(type, listener, {...options, signal: this.controller.signal}); }
  observe(observer, target) { this.observers.push(observer); observer.observe(target); return observer; }
  delay(callback, milliseconds) { const id = setTimeout(() => {this.timers.delete(id); callback();}, milliseconds); this.timers.add(id); return id; }
  destroy() { if (!this.controller) return; this.controller.abort(); this.observers?.forEach(observer => observer.disconnect()); this.timers?.forEach(clearTimeout); this.onDestroy?.(); this.controller = null; }
  disconnectedCallback() { this.destroy(); }
}
export const define = (name, type) => { if (!customElements.get(name)) customElements.define(name, type); };
const modalOwners = new Set();
export function modalState(owner, open) {
  if (open) modalOwners.add(owner); else modalOwners.delete(owner);
  if (modalOwners.size) document.body.dataset.modalOpen = 'true'; else delete document.body.dataset.modalOpen;
  document.dispatchEvent(new CustomEvent('yz:modal-state'));
}
export const root = () => window.Shopify?.routes?.root || (window.YZ?.root || '/').replace(/\/?$/, '/');
export function status(node, message, error = false) { if (!node) return; node.textContent = message; node.toggleAttribute('data-error', error); }
export async function fetchFragment(url, signal) {
  const controller = new AbortController();
  const cancel = () => controller.abort(signal.reason);
  signal?.addEventListener('abort', cancel, {once: true});
  if (signal?.aborted) cancel();
  const timeout = setTimeout(() => controller.abort(new DOMException('Request timed out', 'TimeoutError')), 15000);
  try {
    const response = await fetch(url, {signal: controller.signal});
    if (!response.ok) throw new Error('Fragment unavailable');
    return await response.text();
  } finally { clearTimeout(timeout); signal?.removeEventListener('abort', cancel); }
}
export function openDialog(id, opener) {
  const dialog = document.getElementById(id);
  if (!(dialog instanceof HTMLDialogElement) || !dialog.showModal) return false;
  document.querySelectorAll('dialog[open]').forEach(active => { if (active !== dialog) active.close(); });
  const owner = dialog.closest('yz-dialog');
  if (typeof owner?.open !== 'function') return false;
  owner.open(opener); return true;
}
export class YZDialog extends YZElement {
  init() {
    this.dialog = this.querySelector('dialog'); if (!this.dialog) return;
    this.listen(this.dialog, 'click', event => {
      if (event.target.closest('[data-dialog-close]')) this.dialog.close();
      if (event.target === this.dialog) {
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }
    });
    this.listen(this.dialog, 'close', () => {
      this.unlock(); this.opener?.isConnected && this.opener.focus({preventScroll:true});
      this.opener?.setAttribute('aria-expanded','false');
      this.dispatchEvent(new CustomEvent('yz:dialog-close',{bubbles:true}));
    });
  }
  open(opener) {
    if (this.dialog.open) return;
    this.opener = opener || document.activeElement;
    this.dialog.showModal(); modalState(this, true);
    this.opener?.setAttribute('aria-expanded','true');
    this.dispatchEvent(new CustomEvent('yz:dialog-open',{bubbles:true}));
  }
  unlock() { modalState(this, false); }
  onDestroy() { if (this.dialog?.open) this.dialog.close(); this.unlock(); }
}
define('yz-dialog', YZDialog);
const modules = new Map();
async function loadModules(scope = document) {
  const elements = [...scope.querySelectorAll('[data-yz-module]')];
  if (scope.matches?.('[data-yz-module]')) elements.push(scope);
  for (const element of elements) {
    const name = element.dataset.yzModule, url = window.YZ?.assets?.[name];
    if (url && !modules.has(name)) modules.set(name, import(url).catch(error => {modules.delete(name); if(window.Shopify?.designMode) console.error(error);}));
  }
}
document.addEventListener('click', event => {
  const trigger = event.target.closest('[data-dialog-open]');
  if (trigger && !event.metaKey && !event.ctrlKey && event.button === 0 && openDialog(trigger.dataset.dialogOpen, trigger)) event.preventDefault();
});
document.addEventListener('shopify:section:load', event => loadModules(event.target));
function mediaFailure(image) {
  const area = image.closest('.yz-media,.yz-card-media,.yz-cart-image');
  if (!area || area.querySelector('.yz-media-fallback')) return;
  image.hidden = true;
  area.dataset.mediaFailed = 'true';
  const fallback = document.createElement('span');
  fallback.className = 'yz-media-fallback';
  fallback.textContent = window.YZ.strings.mediaUnavailable;
  area.append(fallback);
}
document.addEventListener('error', event => {
  if (event.target instanceof HTMLImageElement) mediaFailure(event.target);
}, true);
document.querySelectorAll('img').forEach(image => {
  if (image.complete && !image.naturalWidth) mediaFailure(image);
});
for (const type of ['shopify:section:select','shopify:section:deselect','shopify:section:reorder','shopify:block:select','shopify:block:deselect']) {
  document.addEventListener(type,event=>{
    const selector = 'yz-motion, yz-product, yz-menu, yz-information';
    const owners = new Set(event.target.querySelectorAll(selector));
    if (event.target.matches?.(selector)) owners.add(event.target);
    const parent = event.target.closest?.(selector); if (parent) owners.add(parent);
    owners.forEach(element=>element.editorEvent?.(event));
  });
}
document.addEventListener('shopify:section:unload',event=>event.target.querySelectorAll('*').forEach(element=>{ if(element instanceof YZElement) element.destroy(); }));
loadModules();
